/**
 * Shared country info modal for both list and tournament modes.
 * Displays country details, image carousel, and optional rating.
 */

import { CountryImageCarousel } from './tournament/CountryImageCarousel';
import { CountryLocator } from './CountryLocator';
import { CountryDetails } from './CountryDetails';
import type { CountryCode } from '@/data/countries/types';
import { useCountryDisplayData } from '@/hooks/useCountryDisplayData';
import { MapPin } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CountryInfoModalProps {
  code: CountryCode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional rating display */
  rating?: number | null;
  /** Optional custom footer content (e.g., rating picker) */
  footer?: React.ReactNode;
  /** Whether to show the map */
  showMap?: boolean;
}

/**
 * Reusable country info modal component.
 */
export function CountryInfoModal({
  code,
  open,
  onOpenChange,
  rating,
  footer,
  showMap = true,
}: CountryInfoModalProps) {
  // Use shared hook for display data
  const displayData = useCountryDisplayData(code);
  const { country, images } = displayData;

  if (!country) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <span className="text-4xl">{country.flag}</span>
            <div>
              <div>{country.name}</div>
              <div className="text-sm font-normal text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {country.subregion || country.continent}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Map */}
          {showMap && (
            <div className="h-[180px] rounded-lg overflow-hidden border border-border/50">
              <CountryLocator countryName={country.name} countryCode={code} />
            </div>
          )}

          {/* Image Carousel */}
          {images.length > 0 && (
            <CountryImageCarousel
              images={images}
              countryName={country.name}
            />
          )}

          {/* Shared country details component */}
          <CountryDetails data={displayData} />

          {/* Custom footer */}
          {footer}
        </div>
      </DialogContent>
    </Dialog>
  );
}
